import { isNil } from 'lodash';
import { FilterName, LocalDate } from '../../store/models';
import {
  DAY_IN_MS,
  DISPLAY_DATE_FORMAT,
  MINUTES_IN_HOUR,
  MS_IN_MIN,
  SECOND_IN_MS,
  SECONDS_IN_MIN,
} from '../constants/date-config';
import { TIMEZONE_LIST } from '../constants/timezones';
import {
  DatepickerRangeModel,
  DatepickerRangeOutput,
  DatepickerWithTimezoneRangeModel,
} from '../models/datepicker.model';
import { TimezoneModel } from '../models/timezone.model';
import { formatInTimeZone, getTimezoneOffset, toZonedTime } from 'date-fns-tz';
import {
  addDays,
  addMilliseconds,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  endOfDay,
  getHours,
  getMinutes,
  startOfDay,
  startOfMinute,
  subMilliseconds,
} from 'date-fns';

export function getTimestampWithTimezoneOffset(timestamp: number, offset: number = 0): number {
  if (!timestamp) {
    return null;
  }

  return timestamp - offset * MINUTES_IN_HOUR * SECONDS_IN_MIN * SECOND_IN_MS;
}

export function getTimezoneOffsetInHours(timezone: string): number {
  if (isNil(timezone)) {
    return 0;
  }

  return Number(getTimezoneOffset(timezone, new Date()) / MS_IN_MIN / MINUTES_IN_HOUR) || 0;
}

export function getTimezoneOffsetInMilliseconds(timezone: string): number {
  const offsetMinutes = getTimezoneOffset(timezone) / MS_IN_MIN;

  return offsetMinutes * SECONDS_IN_MIN * SECOND_IN_MS;
}

export function getStartOfDay(): number {
  return startOfDay(new Date()).getTime();
}

export function getStartOfDayFromTimestamp(timestamp: number): number {
  return startOfDay(new Date(timestamp)).getTime();
}

export function getEndOfDay(): number {
  return endOfDay(new Date()).getTime();
}

export function getHoursOffsetFromString(offset: string): number {
  if (isNil(offset) || offset === '0') {
    return 0;
  }

  const offsetMinutes = getTimezoneOffset(offset) / MS_IN_MIN;

  return offsetMinutes / MINUTES_IN_HOUR;
}

// Function to normalize a timestamp by removing the timezone offset and return a Date
export function normalizeTimestampToUTCDate(timestamp: number, timezone: string): Date {
  if (!timestamp || !timezone) {
    return null;
  }

  const offsetMilliseconds = getTimezoneOffsetInMilliseconds(timezone);
  const normalizedTimestamp = timestamp - offsetMilliseconds;

  return createDateWithTimezoneOffset(normalizedTimestamp, 'UTC');
}

export function getMinutesOfDay(date: Date): number {
  return getMinutes(date) + getHours(date) * MINUTES_IN_HOUR;
}

export function getMinutesOfDayFromFormValue(value: unknown): number {
  const maybeMoment = value as { hour?: () => number; minute?: () => number };
  if (typeof maybeMoment?.hour === 'function' && typeof maybeMoment?.minute === 'function') {
    return maybeMoment.hour() * MINUTES_IN_HOUR + maybeMoment.minute();
  }
  const d = new Date(+(value as number));
  return d.getUTCHours() * MINUTES_IN_HOUR + d.getUTCMinutes();
}

export function timestampDurationAsString(start: number, end: number): string {
  const duration = differenceInMinutes(end, start);

  const days = differenceInDays(end, start);
  const hours = differenceInHours(end, start) % 24;
  const minutes = duration % 60;

  const daysFormatted = days ? `${days}d ` : '';
  const hoursFormatted = `${hours}h `;
  const minutesFormatted = `${minutes}m`;

  return `${daysFormatted}${hoursFormatted}${minutesFormatted}`;
}

export function getTimestampDiffAsSeconds(start: number, end: number): number {
  return differenceInSeconds(end, start);
}

export function getTimestampDiffAsMinutes(start: number, end: number): number {
  const startTimeTruncated = startOfMinute(new Date(start));
  const endTimeTruncated = startOfMinute(new Date(end));

  const difference = differenceInMinutes(endTimeTruncated, startTimeTruncated);

  return difference !== 0 ? difference : 1;
}

export function timeFormatToMinutes(text: string): number {
  if (!text) {
    return;
  }
  const time = text.split(':').map((textPart: string) => +textPart);
  const [hours, minutes] = time;
  return (hours || 0) * MINUTES_IN_HOUR + (minutes || 0);
}

export function getCurrentDate(): Date {
  return new Date();
}

export function getTomorrowDate(): Date {
  return addDays(new Date(), 1);
}

export function convertGroupTimestampsToDateValues<TimeStampType, DateType>(data: TimeStampType): DateType {
  const result = {} as DateType;
  Object.keys(data).forEach((item: string) => (result[item] = new DatepickerRangeOutput(data[item])));
  return result;
}

export function convertGroupDatesToTimestampValues<DateType, TimeStampType>(data: DateType): TimeStampType {
  const result = {} as TimeStampType;
  Object.keys(data).forEach((item: string) => (result[item] = new DatepickerRangeModel(data[item])));
  return result;
}

export function convertGroupDatesWithTimezoneToTimestampValues<DateType, TimeStampType>(data: DateType): TimeStampType {
  const result = {} as TimeStampType;
  Object.keys(data).forEach((item: string) => (result[item] = new DatepickerWithTimezoneRangeModel(data[item])));
  return result;
}

export function isSomeFilterApplied<FiltersType>(filters: FiltersType): boolean {
  return Object.keys(filters).some((key: FilterName) => {
    const value = filters[key];
    if (value instanceof DatepickerRangeModel) {
      return !(value.start === null && value.end === null);
    }

    if (Array.isArray(value)) {
      return Boolean(value.length);
    }

    return !(value === '' || value === null);
  });
}

export function getTimezoneTextById(timezoneId: string): string {
  return timezoneId
    ? TIMEZONE_LIST.filter((timezone: TimezoneModel) => timezone.utc[0] === timezoneId)[0]?.text || `(${timezoneId})`
    : '';
}

export function formatTimestampInTimezoneToText(timestamp: number, timezone: string): string {
  return formatInTimeZone(timestamp, timezone, DISPLAY_DATE_FORMAT);
}

export function createIsoDateFromLocalDate(localDate: LocalDate): string {
  return new Date(localDate?.timestamp).toISOString();
}

export function createDateWithTimezoneOffset(timestamp: number, timezone: string): Date {
  const offsetInMs = getTimezoneOffset(timezone, new Date(timestamp));
  const adjustedTimestamp = timestamp + offsetInMs;

  return new Date(adjustedTimestamp);
}

export function createDateWithTimezoneFromTimestamp(timestamp: number, timezone: string): Date {
  if (!timestamp || !timezone) {
    return new Date(timestamp);
  }

  return toZonedTime(timestamp, timezone);
}

export function getTimezoneAdjustedTimestamp(dateObj: Date, timezone: string): number {
  return getTimestampWithTimezoneOffset(dateObj.getTime(), -getTimezoneOffsetInHours(timezone));
}

export function getLocalWallClockAsUTCTimestamp(date: Date | number | string): number {
  const d = new Date(date);

  return Date.UTC(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
    d.getMilliseconds(),
  );
}

export function convertDateRangesToWallClockUTC<T extends object>(filter: T): T {
  const result = { ...filter } as Record<string, unknown>;

  Object.entries(result).forEach(([key, value]: [string, unknown]) => {
    if (value && typeof value === 'object' && !Array.isArray(value) && 'start' in value && 'end' in value) {
      const { start, end } = value as { start: Date | number | string; end: Date | number | string };
      result[key] = {
        ...value,
        start: start ? getLocalWallClockAsUTCTimestamp(start) : null,
        end: end ? getLocalWallClockAsUTCTimestamp(end) : null,
      };
    }
  });

  return result as T;
}

export function getCurrentTimeWithTimezone(timezone: string): number {
  return getTimezoneAdjustedTimestamp(new Date(), timezone);
}

export function getDateAsUTC(dateObj: Date): Date {
  return new Date(dateObj.valueOf() + dateObj.getTimezoneOffset() * SECONDS_IN_MIN * SECOND_IN_MS);
}

export function validateAndProcessTimeFrame(timeFrame: DatepickerRangeModel): DatepickerRangeModel {
  const { start, end } = timeFrame;

  if (start && end) {
    return timeFrame;
  }

  if (!start && end) {
    const dayStart = subMilliseconds(end, DAY_IN_MS - 1);
    return new DatepickerRangeModel(new DatepickerRangeOutput({ start: dayStart.getTime(), end }));
  }

  if (start && !end) {
    const dayEnd = addMilliseconds(start, DAY_IN_MS - 1);
    return new DatepickerRangeModel(new DatepickerRangeOutput({ start, end: dayEnd.getTime() }));
  }

  return timeFrame;
}

export function isGmtOffsetFormat(timezone: string): boolean {
  return /^GMT[+-]?\d{1,2}(:\d{2})?$/.test(timezone.trim());
}

export function startOfDayAsUtc(localDate: Date): Date | null {
  if (!localDate) {
    return null;
  }
  const year = localDate.getUTCFullYear();
  const month = localDate.getUTCMonth();
  const day = localDate.getUTCDate();
  return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
}

export function endOfDayAsUtc(localDate: Date): Date | null {
  if (!localDate) {
    return null;
  }
  const year = localDate.getUTCFullYear();
  const month = localDate.getUTCMonth();
  const day = localDate.getUTCDate();
  return new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
}

export function toUtcTimestampFromLocalTime(date: Date, hours: number, minutes: number): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, 0, 0);
}
